import { getLogger } from '../core/logger';
import type { User } from '../core/types';
import { type QuerySnapshot, database } from '../database';
import { removeUndefined } from './firestore-utils';

const logger = getLogger('Firestore');

export const getUsers = async (apartment?: string): Promise<User[]> => {
  const usersCollection = database.collection<User>('users');
  let queryBuilder = usersCollection
    .query()
    .where({ field: 'isApproved', operator: '==', value: true });
  if (apartment) {
    queryBuilder = queryBuilder.where({ field: 'apartment', operator: '==', value: apartment });
  }
  const userSnapshot = await queryBuilder.get();
  return userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
};

export const getAllUsers = async (apartment?: string): Promise<User[]> => {
  const usersCollection = database.collection<User>('users');
  let queryBuilder = usersCollection.query();
  if (apartment) {
    queryBuilder = queryBuilder.where({ field: 'apartment', operator: '==', value: apartment });
  }
  const userSnapshot = await queryBuilder.get();
  return userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  const usersCollection = database.collection<User>('users');
  const queryBuilder = usersCollection
    .query()
    .where({ field: 'email', operator: '==', value: email });
  try {
    const userSnapshot = await queryBuilder.get();
    if (userSnapshot.empty) {
      return null;
    }
    const doc = userSnapshot.docs[0];
    const userData = { id: doc.id, ...doc.data() } as User;
    return userData;
  } catch (error) {
    logger.error('Error querying user by email:', error);
    throw error;
  }
};

export const addUser = async (user: Omit<User, 'id'>): Promise<User> => {
  const usersCollection = database.collection<Omit<User, 'id'>>('users');
  const cleanUser = removeUndefined({ ...user, isApproved: false });
  const docRef = await usersCollection.add(cleanUser);
  return { id: docRef.id, ...cleanUser } as User;
};

export const updateUser = async (id: string, user: Partial<User>): Promise<void> => {
  const userDoc = database.collection<User>('users').doc(id);
  const cleanUser = removeUndefined(user) as Partial<User>;
  await userDoc.update(cleanUser);
};

export const deleteUser = async (id: string): Promise<void> => {
  const userDoc = database.collection<User>('users').doc(id);
  await userDoc.delete();
};

export const subscribeToUsers = async (callback: (users: User[]) => void, apartment?: string) => {
  const filters = [{ field: 'isApproved', operator: '==' as const, value: true }];
  if (apartment) {
    // @ts-expect-error Type mismatch in filter array
    filters.push({ field: 'apartment', operator: '==' as const, value: apartment });
  }
  return database.subscribeToCollection<User>(
    'users',
    (snapshot: QuerySnapshot<User>) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
      callback(users);
    },
    filters
  );
};

export const subscribeToAllUsers = async (
  callback: (users: User[]) => void,
  apartment?: string
) => {
  const filters: Array<{
    field: string;
    operator:
      | '=='
      | '!='
      | '<'
      | '<='
      | '>'
      | '>='
      | 'array-contains'
      | 'in'
      | 'array-contains-any';
    value: unknown;
  }> = [];
  if (apartment) {
    filters.push({ field: 'apartment', operator: '==', value: apartment });
  }
  return database.subscribeToCollection<User>(
    'users',
    (snapshot: QuerySnapshot<User>) => {
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as User);
      callback(users);
    },
    filters
  );
};
